/* eslint-disable jira/choreographer/message-id-by-folder-structure */
/** @jsx jsx */
import React, { memo, useState } from 'react';
import { styled, jsx, css } from '@compiled/react';
import InlineEdit from '@atlaskit/inline-edit';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraPopup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { inlineEditTitleEditViewEntryPoint } from './inline-edit-title/entrypoint.tsx';

export type InlineEditTitleProps = {
	title?: string | null;
	onConfirm: (newTitle: string) => void;
	onValidate: (title: string, newTitle: string) => Promise<string | undefined>;
};

const renderReadView = (title?: string | null, triggerProps?: TriggerProps) => (
	// eslint-disable-next-line @atlaskit/design-system/use-heading
	<h1 css={headingStyles} {...triggerProps}>
		{fg('blu-6305_tab_item_styling_in_horizontal_navigation') ? (
			<ReadViewWithSmallerFont data-testid="horizontal-nav-header.common.ui.read-view">
				{title}
			</ReadViewWithSmallerFont>
		) : (
			<ReadView data-testid="horizontal-nav-header.common.ui.read-view">{title}</ReadView>
		)}
	</h1>
);

export const EditableTitle = memo(({ title, onConfirm, onValidate }: InlineEditTitleProps) => {
	const [editValue, setEditValue] = useState(title);
	const [error, setError] = useState('');
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		inlineEditTitleEditViewEntryPoint,
		{},
	);
	const readViewTrigger = useEntryPointButtonTrigger(entryPointActions);
	return (
		<Box xcss={inlineEditTitleStyles}>
			<InlineEdit
				isRequired
				defaultValue={editValue}
				onEdit={() => setError('')}
				editView={({ errorMessage, ...fieldProps }) => (
					<JiraEntryPointContainer
						id="navigation-kit-header.inline-edit-title-edit-view"
						packageName="jiraNavigationKitHeaderInlineEditTitleEditView"
						entryPointReferenceSubject={entryPointReferenceSubject}
						runtimeProps={{
							errorMessage,
							fieldProps: {
								...fieldProps,
								defaultValue: editValue ?? '',
								value: undefined,
							},
						}}
						fallback={renderReadView(title)}
						errorFallback={() => renderReadView(title)}
					/>
				)}
				readView={() => (
					<Box ref={readViewTrigger}>
						<JiraPopup
							messageId="navigation-kit-inline-edit-title.ui.jira-popup"
							messageType="transactional"
							isOpen={error !== ''}
							content={() => <Box xcss={errorPopupStyles}>{error}</Box>}
							placement="right"
							trigger={(triggerProps) => renderReadView(editValue, triggerProps)}
						/>
					</Box>
				)}
				onConfirm={(newValue) => {
					const value = newValue.trim();
					setEditValue(value);
					onConfirm(value);
				}}
				validate={(newValue) => onValidate(title ?? '', newValue.trim())}
			/>
		</Box>
	);
});

const errorPopupStyles = xcss({
	font: token('font.body.large'),
	padding: 'space.200',
});

const headingStyles = css({
	display: 'grid',
	fontWeight: token('font.weight.bold'),
});

const inlineEditTitleStyles = xcss({
	// Negative margin is added here to make the inline edit field fit in the
	// header without needing to modify the header height
	marginTop: 'space.negative.200',
	marginBottom: 'space.negative.100',
	font: token('font.heading.large'),
	fontWeight: token('font.weight.bold'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadView = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT,
	// Margin to match the size of the edit view
	marginTop: token('space.075'),
	marginBottom: token('space.075'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.large'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadViewWithSmallerFont = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT,
	// Margin to match the size of the edit view
	marginTop: token('space.075'),
	marginBottom: token('space.075'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.medium'),
});
